import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardBody, CardHeader, Col } from "reactstrap";
import getChartColorsArray from "../../Components/Common/ChartsDynamicColor";

const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  month: "short",
  year: "2-digit",
  timeZone: "UTC",
});

const USER_TYPE_LABELS = {
  particular: "Particuliers",
  professional: "Professionnels",
  enterprise: "Entreprises",
  bank: "Banques",
  organism: "Organismes",
  admin: "Administrateurs",
  unknown: "Sans type",
};

function formatMonthLabel(period) {
  const safePeriod = String(period || "");
  const date = new Date(`${safePeriod}-01T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return safePeriod;

  const label = MONTH_LABEL_FORMATTER.format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function getUserTypeLabel(userType) {
  return USER_TYPE_LABELS[userType] || userType;
}

function DashboardInsights({ dashboard }) {
  const barColors = useMemo(
    () => getChartColorsArray('["--vz-primary", "--vz-primary-rgb, 0.20"]'),
    [],
  );
  const donutColors = useMemo(
    () =>
      getChartColorsArray(
        '["--vz-info", "--vz-success", "--vz-warning", "--vz-danger", "--vz-primary", "--vz-secondary", "--vz-dark"]',
      ),
    [],
  );

  const monthlyArticles = useMemo(
    () =>
      Array.isArray(dashboard?.articles_by_month)
        ? dashboard.articles_by_month
        : [],
    [dashboard?.articles_by_month],
  );
  const usersByType = useMemo(
    () => (Array.isArray(dashboard?.users_by_type) ? dashboard.users_by_type : []),
    [dashboard?.users_by_type],
  );
  const articlesByValidation = useMemo(
    () => dashboard?.articles_by_validation || {},
    [dashboard?.articles_by_validation],
  );

  const articlesByMonthSeries = useMemo(
    () => [
      {
        name: "Articles",
        data: monthlyArticles.map((entry) => entry.count || 0),
      },
    ],
    [monthlyArticles],
  );

  const articlesByMonthOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "45%",
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        categories: monthlyArticles.map((entry) => formatMonthLabel(entry.period)),
      },
      yaxis: {
        min: 0,
        forceNiceScale: true,
        labels: {
          formatter(value) {
            return Math.round(value).toString();
          },
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        show: false,
      },
      colors: [barColors[0] || "#405189"],
    }),
    [barColors, monthlyArticles],
  );

  const validationSeries = useMemo(
    () => [
      articlesByValidation.validated || 0,
      articlesByValidation.pending || 0,
    ],
    [articlesByValidation],
  );

  const validationOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
      },
      labels: ["Valides", "En attente"],
      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
      },
      plotOptions: {
        pie: {
          donut: {
            size: "68%",
          },
        },
      },
      colors: [donutColors[1] || "#0ab39c", donutColors[2] || "#f7b84b"],
    }),
    [donutColors],
  );

  const usersByTypeSeries = useMemo(
    () => usersByType.map((entry) => entry.count || 0),
    [usersByType],
  );

  const usersByTypeOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
      },
      labels: usersByType.map((entry) => getUserTypeLabel(entry.userType)),
      legend: {
        position: "bottom",
      },
      dataLabels: {
        enabled: true,
      },
      plotOptions: {
        pie: {
          donut: {
            size: "62%",
          },
        },
      },
      colors: donutColors,
    }),
    [donutColors, usersByType],
  );

  const hasMonthlyArticles = monthlyArticles.some((entry) => (entry?.count || 0) > 0);
  const hasValidationData = validationSeries.some((value) => value > 0);
  const hasUsersByTypeData = usersByTypeSeries.some((value) => value > 0);

  const renderEmptyState = () => (
    <div className="text-muted text-center py-5">
      Aucune donnee disponible pour cette periode.
    </div>
  );

  return (
    <React.Fragment>
      <Col xl={8}>
        <Card>
          <CardHeader>
            <h4 className="card-title mb-0">Articles par mois</h4>
          </CardHeader>
          <CardBody>
            {hasMonthlyArticles ? (
              <ReactApexChart
                options={articlesByMonthOptions}
                series={articlesByMonthSeries}
                type="bar"
                height={320}
                className="apex-charts"
              />
            ) : (
              renderEmptyState()
            )}
          </CardBody>
        </Card>
      </Col>
      <Col xl={4}>
        <Card>
          <CardHeader>
            <h4 className="card-title mb-0">Validation des articles</h4>
          </CardHeader>
          <CardBody>
            {hasValidationData ? (
              <ReactApexChart
                options={validationOptions}
                series={validationSeries}
                type="donut"
                height={320}
                className="apex-charts"
              />
            ) : (
              renderEmptyState()
            )}
          </CardBody>
        </Card>
      </Col>
      <Col xl={12}>
        <Card>
          <CardHeader>
            <h4 className="card-title mb-0">Repartition des utilisateurs</h4>
          </CardHeader>
          <CardBody>
            {hasUsersByTypeData ? (
              <ReactApexChart
                options={usersByTypeOptions}
                series={usersByTypeSeries}
                type="donut"
                height={340}
                className="apex-charts"
              />
            ) : (
              renderEmptyState()
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
}

export default DashboardInsights;
